import React, { useState, useContext, useEffect, useRef } from "react";
import { useTranslation } from "@me8eon/translation";
import "./feedback.scss";
import {
    EonUiInput,
    EonUiLink,
    EonUiTagPill,
} from "@eon-ui/eon-ui-components-react";
import FeedbackCreateIncidentComponent from "./feedback.createIncident.component";
import useSearchStore from "../../store/searchStore";
import { FeedbackProps, LikeOrDislike, useFeedback, extractSourceFromAnswer } from "@me8eon/feedback";
import { AiAssistContext } from "./../ai-assist-refactored/AiAssist.context";
import { useDevModeSelected } from "@me8eon/devmode";
import { useUserData } from "@me8eon/react_authentication";
import { useFeatureFlag } from "@me8eon/featureflags";
import { getUserCommand } from "../ai-assist-refactored/quick-actions/decisionTree";

export type FeedbackData = {
    id: string;
    index: string;
};
export type FeedbackSetterProps = {
    setIsCreateIncidentOpen: (value: boolean) => void,
    setTicketRespLoading: (value: boolean) => void,
    setConfirmTicketCreation: (value: boolean) => void,
};

export type FeedbackGetterProps = {
    isCreateIncidentOpen: boolean,
    ticketRespLoading: boolean,
    confirmTicketCreation: boolean,
};

export function Feedback({
    dataToSend,
    createIncidentHandler,
    aiChatbotIndex,
    showCreateIncident,
    source,
}: FeedbackProps) {
    const {
        showLiveAgentChat,
        setShowLiveAgentChat,
    } = useSearchStore();

    const [likeDislike, setLikeDislike] = useState<LikeOrDislike | undefined>(undefined);
    const [lastLikeDislike, setlastLikeDislike] = useState<LikeOrDislike | undefined>(undefined);

    const t = useTranslation();

    const likeSuggestions = [
        t("chatBot.content"),
        t("chatBot.tone"),
        t("chatBot.information"),
        t("chatBot.complete"),
        t("chatBot.other"),
    ];
    const dislikeSuggestions = [
        t("chatBot.irelevantContent"),
        t("chatBot.lackOfDetail"),
        t("chatBot.incorrectInformation"),
    ];
    const suggestions =
        likeDislike === "dislike" ? dislikeSuggestions : likeSuggestions;
    const [isFeedbackSend, setIsFeedbackSend] = useState(false);
    const [text, setText] = useState("");
    const [choice, setChoice] = useState<string[]>([]);
    const [isCreateIncidentOpen, setIsCreateIncidentOpen] = useState(false);
    const [ticketRespLoading, setTicketRespLoading] = useState(false);
    const [confirmTicketCreation, setConfirmTicketCreation] = useState(false);
    const { actualMessages, handleAiQuery } = useContext(AiAssistContext);
    const feedback = useFeedback();
    const userData = useUserData();
    const [devMode] = useDevModeSelected();
    // const showExplain = devMode === "Explain";
    const feedbackEndRef = useRef<HTMLDivElement>(null);
    const isAiPage = window.location.pathname === "/ai" || window.location.pathname === "/new-ai";

    const setters: FeedbackSetterProps = {
        setIsCreateIncidentOpen,
        setTicketRespLoading,
        setConfirmTicketCreation,
    };
    const getters: FeedbackGetterProps = {
        isCreateIncidentOpen,
        ticketRespLoading,
        confirmTicketCreation,
    };

    useEffect(() => {
        if (likeDislike && feedbackEndRef.current && isAiPage) {
            feedbackEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [likeDislike]);

    const onClick = (likeDislike: LikeOrDislike) => async () => {
        if (lastLikeDislike === likeDislike) {
            setLikeDislike(likeDislike);
            return;
        }
        // if (isFeedbackSend) return;
        const isLike = likeDislike === "like";
        setText("");
        setIsFeedbackSend(isLike);
        setLikeDislike(likeDislike);
        await feedback(await dataToSend(likeDislike));
        setlastLikeDislike(likeDislike);
    };

    const supportButtonsDisabled = showLiveAgentChat || ticketRespLoading || confirmTicketCreation || !isFeedbackSend;

    const submitFeedback = async (btnText?: string) => {
        const currentFeedbackData: any = await dataToSend(likeDislike);
        setIsFeedbackSend(true);
        const answer = actualMessages[actualMessages.length - 1]?.content || currentFeedbackData.answer || "";
        const sourceFromAnswer = extractSourceFromAnswer(answer);

        await feedback({
            source: "freeTextFromChat",
            text: btnText ? btnText : text,
            likeDislike,
            like: likeDislike,
            classification: currentFeedbackData?.classification || undefined,
            query: actualMessages[actualMessages.length - 2]?.content || currentFeedbackData.query || "",
            answer: answer,
            sourceFromAnswer: sourceFromAnswer,
            id: currentFeedbackData.id,
            company: userData.company || "unknown",
            role: userData.currentRole || "unknown"
        });
    }

    return (
        <div className="feedback" style={{ padding: "0 8px" }}>
            <div className="record-feedback">
                <span className="title">{t("chatBot.feedbackTitle")}</span>
                <LikeDislike
                    like={"like"}
                    selected={likeDislike}
                    onClick={onClick("like")}
                    label=""
                    source={source}
                // disabled={isFeedbackSend}
                />
                <LikeDislike
                    like={"dislike"}
                    selected={likeDislike}
                    onClick={onClick("dislike")}
                    label={showCreateIncident ? t("chatBot.feedbackThumbsDownLabel") : ""}
                    source={source}
                // disabled={isFeedbackSend}
                />
            </div>
            {likeDislike && (
                <>
                    {!isFeedbackSend ? (
                        likeDislike === "dislike" && <div className="feedback-form">
                            <div className="label-section">
                                {t(
                                    `chatBot.${likeDislike === "dislike"
                                        ? "wentWrong"
                                        : "wentWell"
                                    }`,
                                )}
                            </div>
                            <div>
                                {suggestions.map(
                                    (item: string, index: number) => (
                                        <EonUiTagPill
                                            filter-name={item}
                                            key={index}
                                            text={item}
                                            class="feedback-tag"
                                            isSelected={choice.includes(item)}
                                            scheme="neutral800"
                                            onClick={() => {
                                                setText(item);
                                                submitFeedback(item);
                                            }}
                                        ></EonUiTagPill>
                                    ),
                                )}
                            </div>
                            <div className="text-area-wrapper">
                                <EonUiInput
                                    className="feedback-input"
                                    value={text}
                                    type="text"
                                    placeholder={t("chatBot.feedbackPrompt")}
                                    onValueChanged={(e) =>
                                        setText(e.target.value)
                                    }
                                    size="small"
                                    trailingIconName="send"
                                    onIconClick={() => submitFeedback()}
                                    onKeyDown={(evt) => {
                                        if (evt.key === "Enter") {
                                            submitFeedback();
                                        }
                                    }}
                                />
                            </div>
                            <div className="respect-privacy">
                                {t("chatBot.privacyMsg")}
                            </div>
                        </div>
                    ) : (
                        <div className="feedback-response">
                            <div className="feedback-message-wrapper">
                                <p className="feedback-message">
                                    {t("chatBot.feedbackMessage")}
                                </p>
                            </div>
                        </div>
                    )}
                    {(aiChatbotIndex === 0 || showCreateIncident) && likeDislike === "dislike" && (
                        <>
                            <div className="issue-resolver-wrapper">
                                <div style={{ color: isFeedbackSend ? "#212121" : "000000" }}>{isFeedbackSend ? t("chatBot.resolveIssueAfterFeedback") : t("chatBot.resolveIssue")}</div>
                                <div className="issue-resolver-content">
                                    <EonUiTagPill
                                        key={"incident"}
                                        text={t("chatBot.quickActions.actions.createIncident")}
                                        title={t("chatBot.quickActions.tooltips.createIncident")}
                                        class="feedback-tag"
                                        scheme="neutral800"
                                        isDisabled={supportButtonsDisabled}
                                        onClick={() => {
                                            if (supportButtonsDisabled) return;
                                            handleAiQuery(getUserCommand("createIncidentSupportBtn"), "quick-actions", { nodeId: "createIncidentSupportBtn" });
                                        }}
                                    ></EonUiTagPill>
                                    <EonUiTagPill
                                        key={"liveAgent"}
                                        text={t("chatBot.quickActions.actions.chatWithServiceDesk")}
                                        title={t("chatBot.quickActions.tooltips.chatWithServiceDesk")}
                                        class="feedback-tag"
                                        scheme="neutral800"
                                        isDisabled={supportButtonsDisabled}
                                        onClick={() => {
                                            if (supportButtonsDisabled) return;
                                            handleAiQuery(getUserCommand("chatWithServiceDeskSupportBtn"), "quick-actions", { nodeId: "chatWithServiceDeskSupportBtn" });
                                        }}
                                    ></EonUiTagPill>
                                    <EonUiTagPill
                                        key={"requestCallback"}
                                        text={t("chatBot.quickActions.actions.requestCallback")}
                                        title={t("chatBot.quickActions.tooltips.requestCallback")}
                                        class="feedback-tag"
                                        scheme="neutral800"
                                        isDisabled={supportButtonsDisabled}
                                        onClick={() => {
                                            if (supportButtonsDisabled) return;
                                            handleAiQuery(getUserCommand("requestCallbackSupportBtn"), "quick-actions", { nodeId: "requestCallbackSupportBtn" });
                                        }}
                                    ></EonUiTagPill>
                                </div>
                            </div>
                            {isCreateIncidentOpen && showCreateIncident && !showLiveAgentChat && (
                                <FeedbackCreateIncidentComponent setters={setters} getters={getters} />
                            )}
                        </>
                    )}
                </>
            )}
            <div ref={feedbackEndRef} />
        </div>
    );
}

export type LikeDislikeProps = {
    like: LikeOrDislike;
    selected: LikeOrDislike | undefined;
    onClick: () => void;
    label: string;
    source: string;
    // disabled: boolean;
};

export function LikeDislike({ like, selected, onClick, label, source }: LikeDislikeProps) {
    const isSelected = selected === like;
    return (
        <div className="like-dislike">
            <EonUiLink
                className={`thumb ${isSelected ? "thumb--selected" : ""} ${like === "like" ? "up" : "down"}`}
                onClick={onClick}
                icon="thumb_up"
                iconPosition="left"
                size="small"
                // disabled={disabled}
                scheme={isSelected ? "red500" : "neutral800"}
            ></EonUiLink>
            {label && <span className="title cursor-pointer" onClick={onClick}>{label}</span>}
        </div>
    );
}
